import * as L from 'leaflet';
import 'leaflet-rotatedmarker';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TimeAgoService } from '@shared/services/time-ago.service';
import {
  POI,
  RouteNetwork,
  RouteNetworkMaintainer,
  RouteNetworkSegment,
  RouteNetworkSegmentLine,
} from '../interfaces/map-data';
import { MapLayer, RouteHighlightRenderers, RouteRenderers } from '../interfaces/map';
import LayerColorUtils from '../utils/layer-color.utils';
import {
  RouteEdgePane,
  RouteSegmentType,
  RouteTextColors,
  WalkingSegmentMarkerPane,
} from '../constants/constants';
import { AddStopElClass, MapTemplateService } from './map-template.service';
import { RoutePlannerService } from './route-planner.service';
import { PoiVisibilityService } from './poi-visibility.service';
import { SanitizationService } from '@shared/services/sanitization.service';

interface SegmentLinePiece {
  lines: RouteNetworkSegmentLine[];
  color: string;
  lastMaintainedTimeUTC?: string;
}

@Injectable({ providedIn: 'root' })
export class LayerBuilderService {
  private translate = inject(TranslateService);
  private timeAgoService = inject(TimeAgoService);
  private mapTemplateService = inject(MapTemplateService);
  private routePlannerService = inject(RoutePlannerService);
  private sanitizationService = inject(SanitizationService);

  private activePulsingMarker: L.Marker | null = null;

  buildRouteNetworkLayers(
    resp: RouteNetwork[],
    renderers: RouteRenderers,
    routeNetworkClickFn?: (
      routeNetwork: Pick<RouteNetwork, 'RouteNetworkID' | 'Latitude' | 'Longitude'>,
      routeSegmentId?: number,
    ) => void,
  ): { networkLayers: MapLayer[]; polylines: MapLayer[] } {
    if (!resp?.length) return { networkLayers: [], polylines: [] };

    const polylines: MapLayer[] = [];
    const networkLayers: MapLayer[] = [];

    resp.forEach((network) => {
      if (this.hasCoords(network)) {
        const marker = this.buildNetworkMarker(network);
        if (routeNetworkClickFn) {
          this.attachClickHandler(marker, routeNetworkClickFn);
        }
        networkLayers.push(marker);
      }
      polylines.push(...this.buildRouteSegments(network, renderers, routeNetworkClickFn));
    });

    return { networkLayers, polylines };
  }

  buildRouteMaintainerLayers(maintainers: RouteNetworkMaintainer[]): MapLayer[] {
    return (maintainers ?? [])
      .filter((m) => m.LastGPSLat && m.LastGPSLon)
      .map((m) => {
        return this.buildRouteMaintainerMarker(m);
      });
  }

  buildPOILayers(poiList: POI[]): MapLayer[] {
    return (poiList || [])
      .filter((poi) => poi.GPSLat && poi.GPSLon)
      .map((poi) => this.buildPOIMarker(poi));
  }

  getRouteMarkerClusterGroupOptions(): L.MarkerClusterGroupOptions {
    return {
      polygonOptions: { fillColor: 'var(--dv-bg-fill-active)', color: 'var(--dv-bg-fill-active)' },
      maxClusterRadius: 30,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 12,
      iconCreateFunction: (cluster: L.MarkerCluster) => {
        const count = cluster.getChildCount();
        const latestMaintainedNetworkLayer = (cluster.getAllChildMarkers() as MapLayer[])
          .filter((marker: MapLayer) => marker.extraData?.lastActiveTime)
          .sort(
            (a, b) =>
              Date.parse(b.extraData!.lastActiveTime!) - Date.parse(a.extraData!.lastActiveTime!),
          )?.[0];

        const routeColor = LayerColorUtils.getRouteColorOrIcon(
          latestMaintainedNetworkLayer?.extraData?.lastActiveTime,
        );

        return L.divIcon({
          html: `
            <div class="route-marker-cluster" style="background:${routeColor}">
              <span style="font-size:14px; color:${RouteTextColors[routeColor]}">${count}</span>
            </div>
          `,
          className: 'route-marker-cluster-wrapper',
          iconAnchor: [12, 12],
          iconSize: L.point(24, 24, true),
        });
      },
    };
  }

  getUserLocationIconLayer(lat: number, lng: number): MapLayer {
    const icon = L.icon({
      iconUrl: 'assets/position_course_simply_48px.svg',
      iconAnchor: [12, 12],
    });

    return L.marker([lat, lng], { icon });
  }

  stopPulse(marker?: L.Marker) {
    const wrapper = (marker ? marker : this.activePulsingMarker)
      ?.getElement()
      ?.querySelector('.leaflet-pulse-wrapper');
    wrapper?.classList.remove('leaflet-pulse-active');
  }

  startPulse(marker: L.Marker) {
    if (this.activePulsingMarker) {
      this.stopPulse(this.activePulsingMarker);
    }
    const wrapper = marker.getElement()?.querySelector('.leaflet-pulse-wrapper');
    wrapper?.classList.add('leaflet-pulse-active');
  }

  private buildPOIMarker(poi: POI): MapLayer {
    const html = this.mapTemplateService.buildPOIMarkerDiv(
      poi.IconColor || 'red',
      poi.IconKey || 'icon-warning',
    );

    const icon = L.divIcon({
      html,
      className: 'poi-marker',
      iconSize: [20, 20],
      iconAnchor: [12, 12],
    });

    const marker: MapLayer = L.marker([poi.GPSLat!, poi.GPSLon!], {
      icon,
      title: poi.POIDescription ?? this.translate.instant('GENERAL.POI'),
      bubblingMouseEvents: false,
      zIndexOffset: 1000,
    })
      .bindPopup(this.mapTemplateService.getPOIPopup(poi), { className: 'general-popup' })
      .bindTooltip(poi.POIDescription ?? this.translate.instant('GENERAL.POI'), {
        permanent: false,
        direction: 'top',
        offset: [0, -12],
        opacity: 0.9,
        className: 'basic-tooltip',
      });

    marker.extraData = {
      isPoiMarker: true,
      poiTypeKey: PoiVisibilityService.getTypeKey(poi),
      origin: 'api',
    };

    return marker;
  }

  private attachClickHandler(
    layer: MapLayer,
    routeNetworkClickFn?: (
      routeNetwork: Pick<RouteNetwork, 'RouteNetworkID' | 'Latitude' | 'Longitude'>,
      routeSegmentId?: number,
    ) => void,
    considerWaypointsBeforeOpen = false,
  ) {
    if (considerWaypointsBeforeOpen && this.routePlannerService.hasWaypoints()) return;

    layer.addEventListener('click', (event: L.LeafletMouseEvent) => {
      routeNetworkClickFn?.(
        {
          RouteNetworkID: (event.target as MapLayer).extraData!.routeNetworkId,
          Latitude: event.latlng.lat,
          Longitude: event.latlng.lng,
        },
        (event.target as MapLayer).extraData!.routeSegmentId,
      );
    });
  }

  private attachPolylineClickHandler(polyline: MapLayer, popup: L.Popup): void {
    polyline.on('popupopen', () => {
      const btn = popup.getElement()?.querySelector(`.${AddStopElClass}`);
      if (!btn) return;

      const hasWaypoints = this.routePlannerService.waypoints().length > 0;
      const label = this.sanitizationService.sanitize(
        hasWaypoints
          ? this.translate.instant('Map.ContinueTourBtnText')
          : this.translate.instant('Map.StartTourBtnText'),
      );

      btn.innerHTML = `<span class="dv-icons">trip</span>${label}`;
    });
  }

  /** walking trails are drawn as dashed lines over a white edge with a footprint marker in the middle,
   * all other segments as solid lines
   */
  buildRouteSegments(
    network: RouteNetwork,
    renderers: RouteRenderers,
    routeNetworkClickFn?: (
      routeNetwork: Pick<RouteNetwork, 'RouteNetworkID' | 'Latitude' | 'Longitude'>,
      routeSegmentId?: number,
    ) => void,
  ): MapLayer[] {
    const segments = network.RouteSegments;
    if (!segments || segments.length === 0) return [];

    const output: MapLayer[] = [];

    for (const segment of segments) {
      const lines = segment.Lines;
      if (!lines || lines.length < 2) continue;

      const isWalking = segment.RouteSegmentTypeID === RouteSegmentType.Walking;
      // dashes restart at every polyline, so walking trails are drawn as one polyline per colour run
      const pieces = this.splitSegmentLines(lines, isWalking);
      if (!pieces.length) continue;

      if (isWalking) {
        output.push(this.buildRouteEdge(network, segment, renderers.edge));

        const walkingMarker = this.buildWalkingSegmentMarker(
          network,
          segment,
          pieces,
          routeNetworkClickFn,
        );
        if (walkingMarker) output.push(walkingMarker);
      }

      for (const piece of pieces) {
        const start = piece.lines[0];
        const polyline: MapLayer = L.polyline(
          piece.lines.map((line) => [line.Latitude!, line.Longitude!] as L.LatLngTuple),
          {
            renderer: renderers.line,
            weight: 4,
            color: piece.color,
            stroke: true,
            ...(isWalking && { dashArray: '10 4', lineCap: 'butt' }),
          },
        );
        const popup = L.popup({ className: 'general-popup', offset: L.point(0, 0) }).setContent(
          this.createSegmentPopupHtml({
            ...network,
            LastMaintainedTimeUTC: piece.lastMaintainedTimeUTC,
          }),
        );

        this.attachClickHandler(polyline, routeNetworkClickFn, true);
        this.attachPolylineClickHandler(polyline, popup);

        polyline.bindPopup(popup);

        polyline.extraData = {
          routeNetworkId: network.RouteNetworkID,
          routeSegmentId: segment.RouteSegmentID ?? start.RouteSegmentID,
          routeSegmentLineId: start.RouteSegmentLineID,
          routeSegmentTypeId: segment.RouteSegmentTypeID,
          routeSegmentFeatures: segment.RouteSegmentFeatures,
          segmentLines: lines,
          origin: 'api',
        };

        output.push(polyline);
      }
    }

    return output;
  }

  buildRouteHighlightLayers(
    polylines: L.Polyline[],
    renderers: RouteHighlightRenderers,
  ): MapLayer[] {
    const haloColor =
      getComputedStyle(document.documentElement).getPropertyValue('--dv-text').trim() || '#1F1F1F';
    const halos: MapLayer[] = [];
    const casings: MapLayer[] = [];
    const lines: MapLayer[] = [];
    const segmentIds = new Set<number | undefined>();

    for (const polyline of polylines as (L.Polyline & MapLayer)[]) {
      const extraData = polyline.extraData;
      const isWalking = extraData?.routeSegmentTypeId === RouteSegmentType.Walking;

      if (!segmentIds.has(extraData?.routeSegmentId)) {
        segmentIds.add(extraData?.routeSegmentId);

        const segmentLatLngs = (extraData?.segmentLines ?? [])
          .filter((line) => this.hasCoords(line))
          .map((line) => [line.Latitude!, line.Longitude!] as L.LatLngTuple);
        // halo pane is 35 % opaque, so overlapping halos do not darken
        halos.push(this.buildHighlightPath(segmentLatLngs, renderers.halo, 14, haloColor));
        casings.push(
          this.buildHighlightPath(segmentLatLngs, renderers.casing, isWalking ? 10 : 12, '#FFFFFF'),
        );
      }

      lines.push(
        this.buildHighlightPath(
          polyline.getLatLngs() as L.LatLng[],
          renderers.line,
          6,
          polyline.options.color!,
          isWalking ? { dashArray: '16 6', lineCap: 'butt' } : {},
        ),
      );
    }

    return [...halos, ...casings, ...lines];
  }

  private buildHighlightPath(
    latLngs: L.LatLngExpression[],
    renderer: L.Renderer | undefined,
    weight: number,
    color: string,
    options: L.PolylineOptions = {},
  ): MapLayer {
    const path: MapLayer = L.polyline(latLngs, {
      renderer,
      pane: renderer?.options.pane,
      weight,
      color,
      opacity: 1,
      lineCap: 'round',
      lineJoin: 'round',
      interactive: false,
      ...options,
    });
    path.extraData = { isRouteHighlight: true, origin: 'api' };
    return path;
  }

  /** one unbroken white edge under the whole segment */
  private buildRouteEdge(
    network: RouteNetwork,
    segment: RouteNetworkSegment,
    renderer?: L.Renderer,
  ): MapLayer {
    const edge: MapLayer = L.polyline(
      (segment.Lines ?? [])
        .filter((line) => this.hasCoords(line))
        .map((line) => [line.Latitude!, line.Longitude!] as L.LatLngTuple),
      {
        renderer,
        pane: RouteEdgePane,
        weight: 6,
        color: '#FFFFFF',
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round',
        interactive: false,
      },
    );

    edge.extraData = {
      routeNetworkId: network.RouteNetworkID,
      routeSegmentId: segment.RouteSegmentID,
      isRouteEdge: true,
      origin: 'api',
    };

    return edge;
  }

  /** footprint marker at the middle of the whole segment, in the grooming colour at that point.
   * Created hidden, MapLayerProcessingService.updateWalkingSegmentMarkers decides when it is shown.
   * A click opens the same popup and side sheet as a click on the trail.
   */
  private buildWalkingSegmentMarker(
    network: RouteNetwork,
    segment: RouteNetworkSegment,
    pieces: SegmentLinePiece[],
    routeNetworkClickFn?: (
      routeNetwork: Pick<RouteNetwork, 'RouteNetworkID' | 'Latitude' | 'Longitude'>,
      routeSegmentId?: number,
    ) => void,
  ): MapLayer | undefined {
    const steps = pieces.flatMap((piece) =>
      piece.lines.slice(1).map((end, i) => ({
        start: L.latLng(piece.lines[i].Latitude!, piece.lines[i].Longitude!),
        end: L.latLng(end.Latitude!, end.Longitude!),
        color: piece.color,
        lastMaintainedTimeUTC: piece.lastMaintainedTimeUTC,
      })),
    );
    const stepLengths = steps.map((step) => step.start.distanceTo(step.end));
    const segmentLength = stepLengths.reduce((sum, length) => sum + length, 0);
    if (!segmentLength) return undefined;

    let remaining = segmentLength / 2;
    let middleIndex = stepLengths.findIndex((length) => {
      if (remaining <= length) return true;
      remaining -= length;
      return false;
    });
    if (middleIndex === -1) middleIndex = steps.length - 1;

    const middleStep = steps[middleIndex];
    const ratio = stepLengths[middleIndex] ? Math.min(remaining / stepLengths[middleIndex], 1) : 0;
    const middle = L.latLng(
      middleStep.start.lat + (middleStep.end.lat - middleStep.start.lat) * ratio,
      middleStep.start.lng + (middleStep.end.lng - middleStep.start.lng) * ratio,
    );

    const marker: MapLayer = L.marker(middle, {
      icon: L.divIcon({
        html: this.mapTemplateService.buildWalkingSegmentMarkerDiv(middleStep.color),
        className: '',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      }),
      pane: WalkingSegmentMarkerPane,
      keyboard: false,
      opacity: 0,
    });
    const popup = L.popup({ className: 'general-popup', offset: L.point(0, 0) }).setContent(
      this.createSegmentPopupHtml({
        ...network,
        LastMaintainedTimeUTC: middleStep.lastMaintainedTimeUTC,
      }),
    );

    this.attachClickHandler(marker, routeNetworkClickFn, true);
    this.attachPolylineClickHandler(marker, popup);

    marker.bindPopup(popup);

    marker.extraData = {
      routeNetworkId: network.RouteNetworkID,
      routeSegmentId: segment.RouteSegmentID,
      isWalkingSegmentMarker: true,
      segmentLengthMeters: segmentLength,
      origin: 'api',
    };

    return marker;
  }

  /** splits segment lines into drawable pieces: one per GPS piece,
   * or one per run of the same colour when mergeSameColor is set
   */
  private splitSegmentLines(
    lines: RouteNetworkSegmentLine[],
    mergeSameColor: boolean,
  ): SegmentLinePiece[] {
    const pieces: SegmentLinePiece[] = [];

    for (let i = 0; i < lines.length - 1; i++) {
      const start = lines[i];
      const end = lines[i + 1];

      if (!this.hasCoords(start) || !this.hasCoords(end)) continue;

      const color = LayerColorUtils.getRouteColorOrIcon(start.LastMaintainedTimeUTC);
      const last = pieces[pieces.length - 1];

      if (mergeSameColor && last?.color === color && last.lines[last.lines.length - 1] === start) {
        last.lines.push(end);
        // popup of a run shows its newest grooming
        if (
          (Date.parse(start.LastMaintainedTimeUTC ?? '') || 0) >
          (Date.parse(last.lastMaintainedTimeUTC ?? '') || 0)
        ) {
          last.lastMaintainedTimeUTC = start.LastMaintainedTimeUTC;
        }
        continue;
      }

      pieces.push({
        lines: [start, end],
        color,
        lastMaintainedTimeUTC: start.LastMaintainedTimeUTC,
      });
    }

    return pieces;
  }

  private buildNetworkMarker(network: RouteNetwork): MapLayer {
    const iconUrl = LayerColorUtils.getRouteColorOrIcon(
      network.LastMaintainedTimeUTC,
      network.CustomerID,
      true,
    );

    const icon = L.divIcon({
      className: '',
      html: `<div class="leaflet-pulse-wrapper">
              <img src="${iconUrl}" width="28" height="28">
            </div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });

    const marker: MapLayer = L.marker([network.Latitude!, network.Longitude!], { icon });
    marker.on('click', () => {
      if (this.activePulsingMarker) {
        this.stopPulse(this.activePulsingMarker);
      }

      this.startPulse(marker as L.Marker);
      this.activePulsingMarker = marker as L.Marker;
    });

    marker.extraData = {
      isRouteNetworkMarker: true,
      name: network.Name,
      routeNetworkId: network.RouteNetworkID,
      lastActiveTime: network.LastMaintainedTimeUTC,
      origin: 'api',
    };
    return marker;
  }

  private buildRouteMaintainerMarker(maintainer: RouteNetworkMaintainer): MapLayer {
    const maintainerColorIcon = LayerColorUtils.getRouteMaintainerColorAndIconUrl(
      maintainer.RouteNetworkMaintainerTypeID,
      maintainer.LastPositionTimeUTC,
    );

    const html = this.mapTemplateService.buildMaintainerMarkerImg(maintainerColorIcon.iconUrl, 56);
    const icon = this.buildCircleDivIcon(html, 56);

    const marker: MapLayer = L.marker([maintainer.LastGPSLat!, maintainer.LastGPSLon!], {
      icon,
      rotationAngle: maintainer.LastCourse || 0,
      title: maintainer.Description ?? this.translate.instant('General.RouteMaintainer'),
      bubblingMouseEvents: false,
    }).bindPopup(this.createRouteMaintainerPopup(maintainer), { className: 'general-popup' });

    marker.extraData = { trackerObjectID: maintainer.TrackerObjectID };

    return marker;
  }

  createSegmentPopupHtml(network: RouteNetwork): string {
    const iconUrl = LayerColorUtils.getRouteColorOrIcon(
      network.LastMaintainedTimeUTC,
      network.CustomerID,
      true,
    );

    return this.mapTemplateService.getMaintainerOrNetworkPopup(
      (network.Name || network.Description) ?? this.translate.instant('General.RouteNetwork'),
      this.timeAgoService.timeAgo(network.LastMaintainedTimeUTC),
      `<img src="${iconUrl}" style="width=28px; height: 28px" />`,
      network.CustomerID,
      undefined,
      true,
    );
  }

  private createRouteMaintainerPopup(maintainer: RouteNetworkMaintainer): string {
    const maintainerColorIcon = LayerColorUtils.getRouteMaintainerColorAndIconUrl(
      maintainer.RouteNetworkMaintainerTypeID,
      maintainer.LastPositionTimeUTC,
    );

    return this.mapTemplateService.getMaintainerOrNetworkPopup(
      maintainer.Description ?? '',
      this.timeAgoService.timeAgo(maintainer.LastPositionTimeUTC),
      `<img src="${maintainerColorIcon.iconUrl}" style="width=48px; height: 48px" />`,
      maintainer.CustomerID,
      'General.LastActivity',
    );
  }

  private buildCircleDivIcon(html: string, iconSize = 24): L.DivIcon {
    return L.divIcon({
      html,
      className: 'route-net-marker',
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconSize / 2, iconSize / 2],
    });
  }

  private hasCoords(obj: { Latitude?: number | null; Longitude?: number | null }): boolean {
    return !!obj.Latitude && !!obj.Longitude;
  }
}
